import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, MapPin } from "lucide-react";
import axios from "axios";
import Navbar from "components/layout/Navbar";
import Footer from "components/layout/Footer";
import ProductCard from "components/product/ProductCard";
import { CATEGORIES } from "constants/categories";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const productsRes = await axios.get(`${API}/products?limit=8`);
        setProducts(productsRes.data.products);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8 },
  };

  const staggerContainer = {
    whileInView: {
      transition: { staggerChildren: 0.1 },
    },
  };

  return (
    <div className="bg-[#F4EBE1] text-[#4F3535] min-h-screen">
      <Navbar />

      {/* Hero Section */}
      <section className="relative flex items-center pt-32 pb-16 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <motion.div
              className="text-center md:text-left flex flex-col items-center md:items-start"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
            >
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-medium text-[#4F3535] leading-[1.1] mb-8">
                Timeless Style <br className="hidden md:block" />
                for the Modern Woman
              </h1>

              <div className="flex items-center gap-4 mb-8 w-full justify-center md:justify-start">
                <div className="h-px bg-[#4F3535]/20 w-12 md:w-24"></div>
                <Heart className="w-4 h-4 text-[#6A4B4B] fill-[#6A4B4B]" />
                <div className="h-px bg-[#4F3535]/20 w-12 md:w-24"></div>
              </div>

              <p className="font-serif italic text-2xl text-[#4F3535] mb-12">
                Where class meets confidence.
              </p>

              <Link
                to="/shop"
                className="bg-[#6A4B4B] text-white hover:bg-[#4F3535] transition-colors duration-300 px-12 py-5 rounded-full tracking-wide text-lg font-medium shadow-md"
                data-testid="shop-now-btn"
              >
                Shop Collection
              </Link>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              className="relative flex justify-center md:justify-end"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.5 }}
            >
              <img
                src="https://github.com/joanrunyiri/jojos_catalogue/blob/main/heroimage.webp?raw=true"
                alt="Elegant clothing"
                className="w-full max-w-md lg:max-w-lg h-[65vh] object-cover shadow-lg rounded-sm"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* New Arrivals Catalogue */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-sm tracking-[0.3em] uppercase text-[#4F3535] font-semibold mb-6">
              Curated for You
            </h2>
            <div className="flex items-center justify-center gap-4">
              <div className="h-px bg-[#4F3535]/20 w-16"></div>
              <Heart className="w-4 h-4 text-[#6A4B4B] fill-[#6A4B4B]" />
              <div className="h-px bg-[#4F3535]/20 w-16"></div>
            </div>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-[#4F3535]/5 mb-4"></div>
                  <div className="h-4 bg-[#4F3535]/10 w-3/4 mb-2 mx-auto"></div>
                  <div className="h-4 bg-[#4F3535]/10 w-1/4 mx-auto"></div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <motion.div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 gap-y-20"
                variants={staggerContainer}
                initial="initial"
                whileInView="whileInView"
                viewport={{ once: true }}
              >
                {products.map((product) => (
                  <motion.div
                    key={product.product_id}
                    variants={{
                      initial: { opacity: 0, y: 20 },
                      whileInView: {
                        opacity: 1,
                        y: 0,
                        transition: { duration: 0.8 },
                      },
                    }}
                  >
                    <ProductCard product={product} />
                  </motion.div>
                ))}
              </motion.div>

              <div className="mt-16 text-center">
                <Link
                  to="/shop"
                  className="inline-block border border-[#4F3535] text-[#4F3535] hover:bg-[#4F3535] hover:text-white transition-colors duration-300 px-12 py-5 rounded-full tracking-wide text-lg font-medium"
                >
                  View More
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Why Shop With Us */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 text-center divide-y md:divide-y-0 md:divide-x divide-[#4F3535]/10">
            <motion.div
              className="flex flex-col items-center pt-8 md:pt-0"
              {...fadeInUp}
            >
              <div className="w-12 h-12 mb-6 flex items-center justify-center">
                {/* Measuring Tape Icon SVG Placeholder */}
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#4F3535"
                  strokeWidth="1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="6" width="20" height="12" rx="2" />
                  <path d="M6 6v4" />
                  <path d="M10 6v4" />
                  <path d="M14 6v4" />
                  <path d="M18 6v4" />
                </svg>
              </div>
              <h3 className="text-sm tracking-[0.1em] uppercase text-[#4F3535] font-medium mb-2">
                Accurate Measurements
              </h3>
              <p className="text-[#8A7B7B] text-sm uppercase tracking-wider">
                On Every Item
              </p>
            </motion.div>

            <motion.div
              className="flex flex-col items-center pt-8 md:pt-0"
              {...fadeInUp}
            >
              <div className="w-12 h-12 mb-6 flex items-center justify-center">
                {/* Quality Badge Icon SVG Placeholder */}
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#4F3535"
                  strokeWidth="1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 15l-2 5l9-9l-9-9l2 5l-9 9l9 9z" />
                  <circle cx="12" cy="10" r="5" />
                </svg>
              </div>
              <h3 className="text-sm tracking-[0.1em] uppercase text-[#4F3535] font-medium mb-2">
                Quality Pieces
              </h3>
              <p className="text-[#8A7B7B] text-sm uppercase tracking-wider">
                You'll wear on repeat
              </p>
            </motion.div>

            <motion.div
              className="flex flex-col items-center pt-8 md:pt-0"
              {...fadeInUp}
            >
              <div className="w-12 h-12 mb-6 flex items-center justify-center">
                {/* Truck Icon SVG Placeholder */}
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#4F3535"
                  strokeWidth="1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 18H3a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h11v14h-2" />
                  <path d="M14 18h-2" />
                  <path d="M14 6h4l4 4v8h-3" />
                  <circle cx="18" cy="18" r="2" />
                  <circle cx="7" cy="18" r="2" />
                </svg>
              </div>
              <h3 className="text-sm tracking-[0.1em] uppercase text-[#4F3535] font-medium mb-2">
                Fast Delivery
              </h3>
              <p className="text-[#8A7B7B] text-sm uppercase tracking-wider">
                Across Kenya
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pick Up Mtaani */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6 text-center flex flex-col md:flex-row items-center justify-center gap-6">
          <div className="w-16 h-16 rounded-full border border-[#4F3535] flex items-center justify-center flex-shrink-0">
            <MapPin className="w-6 h-6 text-[#4F3535]" strokeWidth={1.5} />
          </div>
          <div className="text-left">
            <h3 className="text-base tracking-[0.1em] uppercase text-[#4F3535] font-medium mb-2">
              Pick Up Mtaani
            </h3>
            <p className="text-[#4F3535] text-sm">
              Collect your order from a pickup location near you or choose{" "}
              <strong>doorstep</strong> delivery at checkout.
            </p>
          </div>
        </div>
      </section>

      {/* Categories Grid (Editorial) */}
      <section className="pt-8 pb-0">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-1 p-1">
          {CATEGORIES.map((cat, i) => (
            <Link
              key={i}
              to={`/shop/${cat.id}`}
              className="group relative aspect-[3/4] overflow-hidden bg-[#4F3535]/5 block"
            >
              <img
                src={cat.img}
                alt={cat.name}
                className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-all duration-700 group-hover:scale-105 mix-blend-multiply"
              />
              <div className="absolute inset-0 flex items-end justify-center pb-8 bg-gradient-to-t from-[#4F3535]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <span className="text-white uppercase tracking-[0.2em] text-sm font-medium">
                  {cat.name}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
